import { Form } from '@ampol/design-system';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { transferSingleCard } from 'actions/cardActions';
import { getTransferCardTargetAccountsRequest, getTransferCardTargetAccountsReset } from 'actions/accountActions';
import { yupDateTransformer } from 'utilities/dayjs-utilities';
import { setCardDefaultButton, setTransferCardButtons } from 'actions/modalMetaActions';
import { transferBatchCards } from 'actions/cardActions';
import { getAccountOptions } from 'utilities/account-utilities';

import './transfer-cards-form.scss';
import * as yup from 'yup';
import DeprecatedIcon from '../../../../assets/svg-icons';
import ModalForm from 'components/_global/modal-form/modal-form';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import dayjs from 'dayjs';
import { DATE_FORMAT, DATE_FORMAT_HYPHEN } from 'utilities/constants';
import Label from 'constants/labels';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';

interface TransferCardsFormProps {
  closeForm;
  cardSelection?;
  inModal?;
  isEdit?;
  onEditTransferSubmit?;
}

/**
 * Describe component here
 * @param props
 */
const TransferCardsForm = ({
  closeForm,
  cardSelection,
  inModal,
  isEdit,
  onEditTransferSubmit,
}: TransferCardsFormProps) => {
  const dispatch = useDispatch();

  const transferCardTargetAccounts = useSelector((state: any) => state.accountsStore.transferCardTargetAccounts);
  const transferCardTargetAccountsLoading = useSelector(
    (state: any) => state.accountsStore.transferCardTargetAccountsLoading
  );
  const cardDetail = useSelector((state: any) => state.cardDetailView.cardDetail);
  const assignedGrandfatherClientNumber = useSelector((state: any) => state.userStore.assignedGrandfatherClientNumber);
  const assignedAccountNumber = useSelector((state: any) => state.userStore.assignedAccountNumber);
  const readOnly = !useSelector((state: any) => state.userStore.canReadWriteCards);
  const loggedInUser = useSelector((state: any) => state.userStore.loggedInUser);
  const [isStandaloneAccount, setIsStandaloneAccount] = useState(false);

  const [isFormValid, setIsFormValid] = useState(false);

  const options = getAccountOptions(transferCardTargetAccounts);
  const minDate = isEdit
    ? dayjs().add(1, 'day').startOf('day').format(DATE_FORMAT)
    : dayjs().startOf('day').format(DATE_FORMAT);
  const maxDate = dayjs().add(60, 'd').format(DATE_FORMAT);
  const formId = 'transfer-cards-form';

  const validationSchema = yup.object().shape({
    targetTopLevelAccount: yup.string().nullable().required(Validation.TOP_LEVEL_ACC_REQUIRED),
    contractNumber: yup
      .string()
      .nullable()
      .required(Validation.TARGET_ACC_REQUIRED)
      .notOneOf(
        cardSelection
          ? Object.values(cardSelection)
              .filter((item) => (item as any).isSelected)
              .map((item) => (item as any).accountNumber)
          : [cardDetail?.contractNumber],
        Validation.UNIQUE_TARGET_ACC
      ),
    transferDate: yup
      .date()
      .nullable()
      .required(Validation.TRANSFER_DATE_REQUIRED)
      .transform(yupDateTransformer)
      .min(minDate, isEdit ? Validation.MIN_EDIT_TRANSFER_DATE : Validation.MIN_ADD_TRANSFER_DATE)
      .max(maxDate, Validation.MAX_TRANSFER_DATE),
  });

  const initialValues = {
    targetTopLevelAccount: '',
    contractNumber: '',
    transferDate: minDate,
  };

  const [defaultValues, setDefaultValues] = useState(initialValues);

  useEffect(() => {
    dispatch(getTransferCardTargetAccountsReset());
  }, []);

  useEffect(() => {
    if (defaultValues.targetTopLevelAccount) {
      const gpAccount = loggedInUser.accounts.find((acc) => acc.accountNumber === defaultValues.targetTopLevelAccount);
      setIsStandaloneAccount(gpAccount?.level === 'C');

      setDefaultValues({
        ...defaultValues,
        contractNumber: null,
      });
      dispatch(
        getTransferCardTargetAccountsRequest(defaultValues.targetTopLevelAccount, gpAccount.grandParentAccountNumber)
      );
    }
  }, [defaultValues.targetTopLevelAccount]);

  useEffect(() => {
    if (transferCardTargetAccounts?.length === 1 && isStandaloneAccount) {
      setDefaultValues({
        ...defaultValues,
        contractNumber: transferCardTargetAccounts[0].contractNumber,
      });
    }
  }, [transferCardTargetAccounts]);

  useEffect(() => {
    if (inModal) {
      dispatch(setTransferCardButtons({ inactive: !isFormValid, formId: readOnly ? undefined : formId }));
    }

    return () => {
      inModal && dispatch(setCardDefaultButton());
    };
  }, [isFormValid]);

  const handleSubmitClick = (values) => {
    const transferDate = dayjs(values.transferDate).isSame(dayjs(minDate), 'day')
      ? null
      : dayjs(values.transferDate).format(DATE_FORMAT_HYPHEN);

    const targetGrandParentAccountNumber = loggedInUser?.accounts?.find(
      (x) => x.accountNumber === values?.targetTopLevelAccount
    )?.grandParentAccountNumber;

    if (inModal) {
      dispatch(
        transferSingleCard(
          cardDetail.cardNumber,
          cardDetail.contractNumber,
          values.contractNumber,
          transferDate,
          assignedGrandfatherClientNumber,
          targetGrandParentAccountNumber
        )
      );
      window.dataLayer.push({
        event: 'Card_Portal_Individual_Card_Detail_Transfer_Card',
        button_click: true,
      });
      trackCustomEvent(
        'Submit Transfer Card',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit from Card Detail Modal to submit Transfer Card.',
        { selectedCard: cardDetail.cardNumber }
      );
    } else if (isEdit) {
      onEditTransferSubmit(
        values.contractNumber,
        transferDate,
        Object.values(cardSelection)
          .filter((x) => (x as any).isSelected)
          .map((x) => (x as any).cardNumber),
        targetGrandParentAccountNumber
      );
    } else {
      dispatch(
        transferBatchCards(
          cardSelection,
          values.contractNumber,
          transferDate,
          assignedGrandfatherClientNumber,
          targetGrandParentAccountNumber
        )
      );
      trackCustomEvent(
        'Submit Transfer Card',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to submit Transfer Card(s).',
        { selectedCard: cardSelection }
      );
    }

    closeForm();
  };

  const handleStateChange = (obj) => {
    setIsFormValid(obj.isValid);
  };

  const handleChange = (values) => {
    setDefaultValues({
      ...initialValues,
      ...values,
    });
  };

  return (
    <div className={'transfer-cards-form'}>
      <ModalForm
        title={isEdit ? Label.EDIT_CARD : Label.TRANSFER_CARD}
        closeForm={closeForm}
        inModal={inModal}
        canSubmit={!readOnly && isFormValid}
        formId={formId}
        submitClick={undefined}
      >
        <Form
          id={formId}
          schema={validationSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmitClick}
          onStateChange={handleStateChange}
          onChange={handleChange}
        >
          <Form.DropdownSearch
            label={'Select Top Level Account (Required)'}
            name={'targetTopLevelAccount'}
            style={{ maxWidth: '400px' }}
            data={loggedInUser.accounts
              .filter((acc) => !acc.grandParentAccountNumber.startsWith('05'))
              .map((acc) => ({
                label: acc.name,
                value: acc.accountNumber,
              }))}
            disabled={readOnly}
            type={'dropdown'}
            searchOptions
            allowClear={false}
          />
          <Form.DropdownSearch
            label={'Target Account (Required)'}
            name={'contractNumber'}
            data={options}
            placeholder={'Select Account'}
            loading={transferCardTargetAccountsLoading}
            style={{ maxWidth: '400px' }}
            textOverflow='normal'
            disabled={readOnly}
            searchOptions
          />
          <Form.DatePicker
            label={'Transfer Date'}
            name={'transferDate'}
            style={{ maxWidth: '400px' }}
            disabled={readOnly}
            inputSuffix={
              <SuffixWrapper>
                <DeprecatedIcon iconName='calender' />
              </SuffixWrapper>
            }
          />
        </Form>
      </ModalForm>
    </div>
  );
};

export default TransferCardsForm;
